import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { QueryClientProvider, useQuery, type QueryClient } from "@tanstack/react-query";
import { ANONYMOUS_SESSION, type SessionView } from "@makit/shared";

import { fetchSession, logout, setUnauthorizedHandler } from "../lib/api";
import { forgetAllJobs } from "../lib/job-store";
import { LOGIN_PATH, PUBLIC_PATHS } from "../screens/public/paths";

/** **로그인이 게이트다 (PRD `7.7.1` 확정).**
 *
 * `/jobs` 를 주소창에 직접 쳐도 로그인 화면으로 돌아온다. **로그인하지 않은 셀러가 닿을 수
 * 있는 웹 화면은 로그인 화면(`W-01` · 같은 화면 안의 `W-01b` · `W-01c`)과 로그인 전 화면 넷
 * (`P-01` `/` · `P-02` `/refund` · `P-03` `/terms` · `P-04` `/privacy`)뿐이다.** 그 넷은 이 게이트
 * 밖에 선다(`App.tsx` · `screens/public/paths.ts`). 그 밖의 경로는 모두 이 게이트 안이다.
 *
 * ⚠️ **로그인 여부를 먼저 보고, 그다음에 작업 소유를 본다.** 순서가 뒤집히면 미로그인
 * 셀러에게 `W-11`("그런 작업이 없습니다")을 보이게 되는데, 그건 그 작업이 있는지 없는지를
 * 우리가 알려준 셈이 된다. 그래서 이 게이트가 작업 셸(`/jobs/:jobId`)보다 바깥에 있다.
 *
 * ⛔ **계정이 바뀌면 앞 계정의 것을 한 번도 그리지 않는다 (2026-10-04 개인정보 버그).**
 * 사용자 제보는 「아이디를 다르게 로그인했는데, 이전 아이디로 로그아웃한 화면에서 시작한다」
 * 였다. 원인이 둘이었다.
 *
 * 1. 로그아웃이 **세션 질문 하나만** 다시 물었다. 작업 목록 · 작업 · 청구 내역 · 내 계정 같은
 *    react-query 캐시는 그대로 남아, 같은 탭에서 다른 계정이 로그인하면 그 화면이 **앞 계정의
 *    답을 먼저 그렸다.**
 * 2. 게이트가 세션이 풀린 이유를 가리지 않고 `from`(지금 주소)을 들고 로그인으로 보냈다. 직접
 *    로그아웃한 경우에도 앞 계정이 보던 주소(`/jobs/<앞 계정의 작업>`)가 다음 로그인의 도착지가
 *    됐다.
 *
 * 그래서 이 파일이 네 가지를 한다.
 *
 * - **셀러의 데이터는 계정마다 새로 만든 react-query 클라이언트에 담는다**(`createDataClient`).
 *   계정이 끝나면(로그아웃 · 미로그인 답 · 다른 셀러 확인) **그 클라이언트를 통째로 버리고**
 *   새것으로 바꾼다. 같은 클라이언트를 `clear()` 로 비우기만 하면, 로그아웃 전에 나간 요청의
 *   `onSuccess`(`setQueryData`)가 끝나며 비운 캐시에 **앞 셀러의 작업을 다시 넣는다.** 앞 화면의
 *   클로저는 버린 클라이언트를 쥐고 있으니 거기에 넣고 끝난다. 세션 질문은 따로 고정
 *   클라이언트(`sessionClient`)에 둔다 — 판정이 데이터 클라이언트를 바꾸는 쪽이라 같은 곳에
 *   두면 판정이 제 발밑을 바꾼다.
 * - **직접 로그아웃(`signOut`)은 전부 버리고 돌아갈 주소 없이 `/login` 으로 간다.** 서버
 *   무효화가 실패해도 버린다. 이 탭에서 앞 계정의 흔적을 지우는 것은 서버 답과 무관하다.
 * - **세션 답이 바뀌는 순간을 세션 질문 안에서 판정한다**(`nextSessionStep`). 로그인됨 → 미로그인
 *   이면 버리고, 앞 셀러와 **같은 셀러라고 증명되지 않는** 셀러가 들어오면 버리고 `from` 을
 *   버린다. ⚠️ **판정을 렌더 이펙트로 옮기지 않는다.** 이펙트는 그린 뒤에 돈다 — 새 세션과 앞
 *   계정의 캐시가 함께 한 번 그려진다(앱의 `probeSession` 과 같은 이유).
 * - **버릴 때 게이트 안쪽을 통째로 다시 세운다**(`key={epoch}`). 이미 그려진 화면은 자기 결과를
 *   들고 있다. 다시 세우면 모든 화면이 새 클라이언트에서 새로 묻는다.
 *
 * 401(세션 만료 · 다른 기기에서의 전체 로그아웃)은 같은 사람이 다시 로그인할 가능성이 커서
 * `from` 을 그대로 들고 간다. **로그인 뒤 셀러가 앞과 다르면 그때 버린다**(`loginTarget`). */

const SESSION_KEY = ["auth", "session"] as const;

/** 탭끼리 「셀러가 바뀌었다」만 알리는 통로. **값을 싣지 않는다** — 받은 탭은 서버에 다시
 *  물을 뿐이다. 다른 탭에서 계정을 바꾸면 쿠키가 공유돼 이 탭의 요청이 새 계정으로 나가는데,
 *  화면은 앞 계정의 캐시를 들고 있다. 그 창을 이 통로가 닫는다.
 *
 * ⛔ **셀러가 실제로 바뀐 때(`notify`)와 직접 로그아웃 때만 보낸다.** 새로고침 · 새 탭마다
 * 보내면 열린 탭 전부가 세션을 다시 묻는다. 「증명 안 됨」에서 보내면 받은 탭도 「증명 안 됨」을
 * 받아 다시 보내고, 두 탭이 끝없이 서로를 비운다. */
const SESSION_CHANNEL = "makit.session";

/* ── 판정 시작 (session-switch) ─────────────────────────────────────────────
 * `scripts/web-logout-clears.test.mjs` 가 이 구역만 잘라 타입을 걷고 실제로 부른다. **이 구역
 * 안에서는 import 한 값을 쓰지 않는다**(타입은 걷히므로 괜찮다). */

/** 셀러를 가르는 표지. 서버가 세션 응답에 싣는 `sellerKey` 다(`packages/shared` `SessionView`,
 *  `docs/DATA_DICTIONARY.md` 42장). `Seller.id` 의 파생값이라 이 값으로 계정을 특정할 수 없고,
 *  같은 셀러면 언제나 같다. **이름 · 로그인 수단은 보지 않는다** — 둘이 같은 두 계정이 있다.
 *
 * 서식이 아니면(서버가 이 필드를 싣기 전 판 · api 롤백 · 파생 실패로 `null`) **증명되지 않은
 * 표지**를 돌려준다. 그 표지는 셀러 키와 같지 않다. */
export const UNPROVABLE_SELLER = "unprovable";

/** 직접 로그아웃한 뒤의 자리표. **셀러의 값이 아니다.** 로그아웃 때 저장소의 키를 이것으로
 *  바꿔 둔다. 지우기만 하면 「앞 셀러 없음」으로 읽혀, 뒤로 가기로 앞 셀러의 주소를 `from`
 *  으로 든 채 다른 셀러가 소셜 로그인하면(페이지가 새로 읽힌다) 그 주소로 들어간다. 이 표지는
 *  무엇과도 같지 않아 다음 로그인은 누구든 `from` 을 버린다. */
export const SIGNED_OUT_SELLER = "signed-out";

const SELLER_KEY_FORMAT = /^[0-9a-f]{32}$/;

export function sellerMark(view: SessionView): string | null {
  if (!view.authenticated) return null;
  const key: unknown = (view as { sellerKey?: unknown }).sellerKey;
  return typeof key === "string" && SELLER_KEY_FORMAT.test(key) ? key : UNPROVABLE_SELLER;
}

/** **같다고 증명될 때만 참이다.** 서식에 맞는 두 값이 같을 때뿐이다. 틀리는 방향을 이쪽으로
 *  고른 이유: 같은 셀러를 다르다고 보면 캐시를 한 번 더 묻고 `from` 을 잃을 뿐이지만, 다른
 *  셀러를 같다고 보면 **앞 셀러의 화면을 다음 셀러에게 그린다.** */
export function provablySameSeller(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return false;
  if (!SELLER_KEY_FORMAT.test(a) || !SELLER_KEY_FORMAT.test(b)) return false;
  return a === b;
}

/** 이 탭이 기억하는 것. `lastSeller` 는 **미로그인 답을 받아도 지우지 않는다** — 다음에
 *  들어오는 셀러와 견줄 대상이 그것이다(만료 · 다른 기기 로그아웃 뒤의 재로그인). */
export interface SessionMemory {
  signedIn: boolean;
  lastSeller: string | null;
}

export const EMPTY_SESSION_MEMORY: SessionMemory = Object.freeze({
  signedIn: false,
  lastSeller: null,
});

export interface SessionStep {
  memory: SessionMemory;
  /** 셀러의 데이터를 전부 버린다(데이터 클라이언트를 새로 만든다). */
  wipe: boolean;
  /** 앞 셀러와 같은 셀러라고 증명되지 않는 셀러가 들어왔다. `from` 을 버리고, 게이트 안이면
   *  `/jobs` 로 옮긴다. */
  accountChanged: boolean;
  /** 다른 탭에 알린다. **셀러 키로 다른 셀러임이 확인됐을 때만.** */
  notify: boolean;
}

/** 세션 답 하나를 받았을 때 할 일.
 *
 * - 미로그인 답: 직전이 로그인됨이었으면 버린다. 앞 셀러의 답을 다음 셀러가 볼 수 없게.
 * - 로그인됨 답: 앞 셀러를 모르면(처음 연 탭) 버릴 것이 없다. 앞 셀러가 있었는데(같은 탭의
 *   기억이든, 페이지가 새로 읽혀 저장소에서 꺼낸 것이든) 같다고 증명되지 않으면 버리고 계정이
 *   바뀐 것으로 본다.
 * - ⛔ **「증명 안 됨」이 거듭되면 아무것도 하지 않는다.** 서버가 키를 주지 못하는 동안(웹이
 *   api 보다 먼저 배포됨 · api 롤백 · 파생 실패) 세션을 물을 때마다 「다른 셀러」가 나와,
 *   버리기 · 다시 세우기 · `/jobs` 이동이 매번 돈다. 처음 「증명 안 됨」으로 넘어갈 때 한 번
 *   버렸으면 그 뒤로는 버릴 것도 견줄 것도 없다. 이때는 같은 셀러인지 가를 수단이 없다 —
 *   그 사이 미로그인을 거쳤다면 그때 이미 버렸다. */
export function nextSessionStep(memory: SessionMemory, view: SessionView): SessionStep {
  const mark = sellerMark(view);
  if (!mark) {
    return {
      memory: { signedIn: false, lastSeller: memory.lastSeller },
      wipe: memory.signedIn,
      accountChanged: false,
      notify: false,
    };
  }
  if (mark === UNPROVABLE_SELLER && memory.lastSeller === UNPROVABLE_SELLER) {
    return {
      memory: { signedIn: true, lastSeller: UNPROVABLE_SELLER },
      wipe: false,
      accountChanged: false,
      notify: false,
    };
  }
  const changed = memory.lastSeller !== null && !provablySameSeller(memory.lastSeller, mark);
  return {
    memory: { signedIn: true, lastSeller: mark },
    wipe: changed,
    accountChanged: changed,
    notify: changed && mark !== UNPROVABLE_SELLER,
  };
}

/** 계정이 끝날 때 데이터 클라이언트를 갈아 끼운다. **앞 클라이언트는 비우고 버린다.**
 *
 * 비우기만 하고 계속 쓰면 안 되는 이유(파일 머리말): 앞 셀러 때 나간 요청 · 뮤테이션은 끝나며
 * 자기가 쥔 클라이언트에 답을 넣는다. 그 클라이언트가 지금 화면의 것이면 앞 셀러의 작업이
 * 다음 셀러의 캐시에 들어간다. 버린 것이면 아무 화면에도 닿지 않는다. */
export interface DisposableClient {
  cancelQueries(): Promise<unknown> | unknown;
  clear(): void;
}

export function swapDataClient<C extends DisposableClient>(previous: C, create: () => C): C {
  void Promise.resolve(previous.cancelQueries()).catch(() => undefined);
  previous.clear();
  return create();
}

/** 클라이언트를 이 화면 수명에 붙인다. **떼는 함수를 돌려준다**(`useEffect` 의 정리).
 *
 * ⛔ **세션 클라이언트는 이것 없이는 탭 복귀를 모른다.** query-core v5 는 `QueryClient.mount()`
 * 가 `focusManager` · `onlineManager` 를 구독할 때만 창 포커스와 재연결에 다시 묻는다. 그
 * `mount()` 는 보통 `QueryClientProvider` 가 부르는데, 세션 클라이언트는 그 Provider 밖에서 쓰므로
 * 아무도 부르지 않았다. 그러면 `refetchOnWindowFocus: "always"` 가 죽는다. 탭 A(셀러 X)에서
 * 세션이 만료되고 새 탭 B 에서 셀러 Y 가 로그인한 뒤 A 로 돌아오면, 데이터 클라이언트(Provider
 * 가 붙였다)는 포커스로 작업을 **Y 의 쿠키로** 다시 가져오는데 세션은 묻지 않아 X 의 화면에 Y 의
 * 답이 섞인 채 남는다. */
export interface MountableClient {
  mount(): void;
  unmount(): void;
}

export function attachClient(client: MountableClient): () => void {
  client.mount();
  return () => client.unmount();
}

/** 로그인 뒤의 도착지. **계정이 바뀌었으면 `from` 을 버린다** — 그 주소는 앞 셀러가 보던
 *  자리다. 서버는 남의 작업을 404 로 답하지만, 다음 셀러가 「그런 작업이 없습니다」에서
 *  시작할 이유가 없다. */
export function loginTarget(from: string | undefined, accountChanged: boolean): string {
  if (accountChanged || !from) return "/jobs";
  return from;
}

/** 이 주소가 게이트 안인가. 로그인 화면과 로그인 전 화면은 밖이다. */
export function isGatedPath(pathname: string, openPaths: readonly string[]): boolean {
  return !openPaths.includes(pathname);
}

/** 페이지가 새로 읽혀도(소셜 로그인은 공급자를 다녀오는 최상위 이동이다) 앞 셀러와 견줄 수
 *  있게 **그 표지 하나만** 탭의 `sessionStorage` 에 둔다.
 *
 * ⛔ **이 값 말고는 두지 않는다.** 이름 · 수단 · 주소를 함께 두지 않는다. `localStorage` 를 쓰지
 * 않는 이유는 탭을 닫아도 남기 때문이다(`SessionView.sellerKey` 규약 4). */
export const SELLER_KEY_STORAGE = "makit.sellerKey";

/** 저장소를 못 읽으면 앞 셀러를 모르는 것으로 둔다. 그때 잃는 것은 새로 읽힌 페이지에서의
 *  비교 하나이고, 그 페이지의 캐시는 이미 비어 있다. */
export function readSellerKey(storage: Pick<Storage, "getItem"> | null): string | null {
  try {
    return storage?.getItem(SELLER_KEY_STORAGE) ?? null;
  } catch {
    return null;
  }
}

export function writeSellerKey(storage: Pick<Storage, "setItem"> | null, value: string): void {
  try {
    storage?.setItem(SELLER_KEY_STORAGE, value);
  } catch {
    /* 저장소가 막힌 브라우저다. 같은 탭 안의 기억(메모리)은 그대로 돈다. */
  }
}

/* ── 판정 끝 (session-switch) ───────────────────────────────────────────── */

const OPEN_PATHS: readonly string[] = [LOGIN_PATH, ...Object.values(PUBLIC_PATHS)];

/** 이 탭의 sessionStorage. 막힌 브라우저(일부 사생활 보호 모드)에서는 읽기만으로 던진다. */
function tabStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

interface SessionState {
  session: SessionView;
  /** 아직 모른다. **`false` 로 뭉치지 않는다** — 뭉치면 이미 로그인한 셀러가 새로고침할
   *  때마다 로그인 화면이 한 번 스쳤다가 사라진다. */
  loading: boolean;
  /** 로그인 뒤 다시 묻는다. */
  refresh: () => void;
  /** 직접 로그아웃. **성공 · 실패와 상관없이** 이 탭의 캐시를 전부 버리고 `/login` 으로 간다. */
  signOut: () => Promise<void>;
  /** 직전 로그인이 앞 셀러와 다른 셀러였다. 로그인 화면이 `from` 을 버리는 근거다. */
  accountChanged: boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({
  children,
  sessionClient,
  createDataClient,
}: {
  children: ReactNode;
  /** 세션 질문만 담는 고정 클라이언트. 셀러의 데이터는 여기 두지 않는다. */
  sessionClient: QueryClient;
  /** 셀러의 데이터를 담는 클라이언트를 만든다. 계정이 바뀔 때마다 부른다. */
  createDataClient: () => QueryClient;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(() => ({ client: createDataClient(), epoch: 0 }));
  const dataClient = useRef(data.client);
  const [accountChanged, setAccountChanged] = useState(false);
  // 앞 셀러는 이 탭의 sessionStorage 에서 꺼내 시작한다. 소셜 로그인은 페이지를 새로 읽어
  // 메모리를 잃기 때문이다. 렌더마다 읽지 않게 처음 한 번만 채운다.
  const memory = useRef<SessionMemory | null>(null);
  if (memory.current === null) {
    memory.current = { ...EMPTY_SESSION_MEMORY, lastSeller: readSellerKey(tabStorage()) };
  }
  // 버릴 때마다 오른다. 그 전에 나간 세션 질문의 답은 버린다 — 로그아웃 직전에 나간 질문이
  // 「로그인됨」을 들고 늦게 돌아와 앞 셀러를 다시 앉히는 것을 막는다.
  const generation = useRef(0);
  const pathname = useRef(location.pathname);
  pathname.current = location.pathname;
  const channel = useRef<BroadcastChannel | null>(null);

  const announce = useCallback(() => {
    try {
      channel.current?.postMessage("changed");
    } catch {
      /* 닫힌 통로다. 다른 탭은 창을 다시 볼 때 묻는다(`refetchOnWindowFocus`). */
    }
  }, []);

  /** 셀러의 데이터를 전부 버린다. 데이터 클라이언트를 새것으로 바꾸고 화면을 다시 세운다. */
  const wipeAccountData = useCallback(() => {
    generation.current += 1;
    const fresh = swapDataClient(dataClient.current, createDataClient);
    dataClient.current = fresh;
    setData((previous) => ({ client: fresh, epoch: previous.epoch + 1 }));
    forgetAllJobs();
  }, [createDataClient]);

  const askSession = useCallback(async (): Promise<SessionView> => {
    const asked = generation.current;
    const view = await fetchSession();
    if (asked !== generation.current) return view;

    const before = memory.current ?? EMPTY_SESSION_MEMORY;
    const step = nextSessionStep(before, view);
    memory.current = step.memory;
    // 로그인됨 답의 표지만 적는다. 미로그인 답에서는 지우지 않는다 — 지우면 만료 뒤 다른
    // 셀러가 들어올 때 견줄 것이 없어 앞 셀러의 `from` 으로 들어간다.
    if (view.authenticated && step.memory.lastSeller !== null) {
      writeSellerKey(tabStorage(), step.memory.lastSeller);
    }
    if (view.authenticated) setAccountChanged(step.accountChanged);

    if (step.wipe) {
      wipeAccountData();
      // 답을 먼저 앉힌다. 그래야 다시 세운 화면이 **새 세션으로** 처음 그려진다.
      sessionClient.setQueryData(SESSION_KEY, view);
    }
    // 계정이 바뀌었는데 게이트 안에 있다(다른 탭에서 바꿨다 · 소셜 로그인이 앞 셀러의 주소로
    // 데려왔다). 게이트가 로그인으로 보내지 않으므로 여기서 앞 셀러의 주소를 떠난다.
    if (step.accountChanged && isGatedPath(pathname.current, OPEN_PATHS)) {
      navigate("/jobs", { replace: true });
    }
    if (step.notify) announce();
    return view;
  }, [announce, navigate, sessionClient, wipeAccountData]);

  // 세션 클라이언트를 붙인다. 붙이지 않으면 탭 복귀 · 재연결에 세션을 다시 묻지 않는다(`attachClient`).
  useEffect(() => attachClient(sessionClient), [sessionClient]);

  const query = useQuery(
    {
      queryKey: SESSION_KEY,
      queryFn: askSession,
      // 세션은 요청마다 바뀌는 값이 아니다. 화면을 옮길 때마다 다시 묻지 않는다.
      staleTime: 60_000,
      // 다만 **탭으로 돌아올 때는 늘 묻는다.** 그 사이 다른 탭에서 계정이 바뀌었을 수 있다.
      // 질문 하나가 앞 셀러의 화면을 다음 셀러에게 그리는 것보다 싸다.
      refetchOnWindowFocus: "always",
    },
    sessionClient,
  );

  // ⚠️ **API 가 401 을 주는 순간 이 캐시는 거짓이 된다.** 세션이 만료됐거나 다른 기기에서
  // 전체 로그아웃한 경우이고, 그때 캐시를 그대로 두면 셀러는 **로그인된 화면에서 모든
  // 동작만 조용히 실패하는 상태**를 본다. 다시 물으면 서버가 미인증으로 답하고, 세션 질문이
  // 캐시를 버린 뒤 아래 `RequireAuth` 가 `W-01` 로 돌려보낸다.
  //
  // `cancelRefetch: false`: 한 화면에서 401 이 여럿 와도 질문은 하나만 나간다. 끊고 다시
  // 물으면 끊긴 질문의 판정과 새 질문의 판정이 겹친다.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      void sessionClient.invalidateQueries({ queryKey: SESSION_KEY }, { cancelRefetch: false });
    });
    return () => setUnauthorizedHandler(null);
  }, [sessionClient]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const opened = new BroadcastChannel(SESSION_CHANNEL);
    opened.onmessage = () => {
      void sessionClient.invalidateQueries({ queryKey: SESSION_KEY }, { cancelRefetch: false });
    };
    channel.current = opened;
    return () => {
      channel.current = null;
      opened.close();
    };
  }, [sessionClient]);

  const signOut = useCallback(async () => {
    try {
      await logout();
    } catch {
      // 서버 무효화가 실패해도 이 탭은 버린다. 세션을 다시 물으면 서버가 아직 로그인됨이라고
      // 답할 수 있고, 그때 로그인 화면이 내 작업으로 돌려보낸다 — 그건 거짓이 아니다.
    }
    // **세션 답까지 걷는다.** 세션 질의는 다시 세운 화면에서 새로 서고 다시 묻는다.
    sessionClient.removeQueries({ queryKey: SESSION_KEY });
    wipeAccountData();
    // 저장소의 셀러 표지를 걷고 자리표로 바꾼다(`SIGNED_OUT_SELLER`). 다음 로그인은 누구든
    // 같다고 증명되지 않아 `from` 을 버린다.
    memory.current = { signedIn: false, lastSeller: SIGNED_OUT_SELLER };
    writeSellerKey(tabStorage(), SIGNED_OUT_SELLER);
    setAccountChanged(false);
    announce();
    // **돌아갈 주소를 싣지 않는다.** 게이트에 맡기면 지금 주소를 `from` 으로 들고 가고, 다음에
    // 들어오는 셀러가 그 주소에서 시작한다(이 버그의 2번).
    navigate(LOGIN_PATH, { replace: true });
  }, [announce, navigate, sessionClient, wipeAccountData]);

  const value: SessionState = {
    session: query.data ?? ANONYMOUS_SESSION,
    loading: query.isPending,
    refresh: () => {
      void sessionClient.invalidateQueries({ queryKey: SESSION_KEY });
    },
    signOut,
    accountChanged,
  };
  return (
    <SessionContext.Provider value={value}>
      <QueryClientProvider client={data.client}>
        <Fragment key={data.epoch}>{children}</Fragment>
      </QueryClientProvider>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error("SessionProvider 밖에서 useSession 을 불렀습니다");
  return value;
}

/** 로그인한 셀러만 지나간다. 아니면 `W-01` 로 돌려보낸다. */
export function RequireAuth() {
  const { session, loading } = useSession();
  const location = useLocation();

  // 아직 모르는 동안 아무것도 그리지 않는다. 여기서 로그인 화면을 먼저 그리면, 로그인한
  // 셀러가 새로고침할 때마다 로그인 화면이 한 번 번쩍인다.
  if (loading) return null;
  if (session.authenticated) return <Outlet />;

  // 어디로 가려 했는지를 들고 간다. 서버가 허용 목록으로 한 번 더 거른다 — **여기서
  // 거르는 것만으로는 부족하다.** 이 값은 주소창에서 만들어질 수 있다.
  //
  // 여기 오는 것은 세션이 **스스로 풀린** 경우다(만료 · 다른 기기에서의 로그아웃 · 처음
  // 들어온 주소). 직접 로그아웃은 `signOut` 이 주소 없이 `/login` 으로 먼저 보낸다. 로그인
  // 뒤 다른 셀러였으면 로그인 화면이 이 값을 버린다(`loginTarget`).
  return <Navigate to={LOGIN_PATH} replace state={{ from: location.pathname + location.search }} />;
}
